import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../../../../../redux/hooks/useAppSelector'
import { selectPatientInfo } from '../../../../../../store/charts/chartsSelectors'
import {
  ACCOUNT_CREDITS_TEXT,
  GENERAL_CREDITS_TEXT,
  REFERRAL_CREDITS_TEXT,
  TOTAL_TEXT,
} from '../../../../../../utils/constants'
import { EditIcon } from '@quickmd-llc/design-system'
import { EditCardAccountInfo } from '../../../edit-card-account-info/EditCardAccountInfo'
import { EditCreditModal } from '../edit-credit-modal/EditCreditModal'
import { EditPlanVisitsModal } from '../edit-plan-visits-modal/EditPlanVisitsModal'
import { convertMoneyToUSAFormat } from '../../../../../../utils/common'
import { useProgramPlanPackage } from '@/hooks/useProgramPlanPackage'
import { useCanEditPackage } from '@/hooks/useCanEditPackage'
import { getPlanVisitBalance } from '@/utils/programPlanVisits'
import './AccountCreditsBlock.scss'

type CreditType = 'standard' | 'referral'

export const AccountCreditsBlock = () => {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')

  const patientInfo = useAppSelector(selectPatientInfo)
  const currentPatientInfo = id ? patientInfo?.[id] : undefined
  const isLoading = !currentPatientInfo || !('CreditAmount' in currentPatientInfo)

  const generalAmount = currentPatientInfo?.CreditAmount ?? 0
  const referralAmount = currentPatientInfo?.ReferralCreditAmount ?? 0
  const total = generalAmount + referralAmount

  // The plan row shows only when the patient holds an active Quit 7-OH Plan; the hook already
  // gates on the program flag and returns null otherwise (QMD-4371).
  const programPackage = useProgramPlanPackage(id)
  const planBalance = getPlanVisitBalance(programPackage)
  const canEditPlan = useCanEditPackage()
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false)

  const [openModal, setOpenModal] = useState<CreditType | null>(null)
  const handleCloseModal = useCallback(() => setOpenModal(null), [])

  // Updated synchronously with openModal so creditType stays stable during the close animation.
  const [lastOpenedType, setLastOpenedType] = useState<CreditType>('standard')
  const handleOpenModal = useCallback((type: CreditType) => {
    setLastOpenedType(type)
    setOpenModal(type)
  }, [])

  if (!id) return null

  return (
    <>
      <EditCreditModal
        isOpen={!!openModal}
        creditType={lastOpenedType}
        currentBalance={lastOpenedType === 'standard' ? generalAmount : referralAmount}
        patientId={id}
        onClose={handleCloseModal}
      />

      <EditPlanVisitsModal
        isOpen={isPlanModalOpen}
        patientId={id}
        programPackage={programPackage}
        onClose={() => setIsPlanModalOpen(false)}
      />

      <EditCardAccountInfo title={ACCOUNT_CREDITS_TEXT}>
        <div className='account-credits-block-rows'>
          <div className='account-credits-block-row'>
            <div className='account-credits-block-field'>
              <span className='account-credits-block-label'>{GENERAL_CREDITS_TEXT}</span>
              <span className='account-credits-block-amount'>
                {isLoading ? '—' : convertMoneyToUSAFormat.format(generalAmount)}
              </span>
            </div>
            <button
              type='button'
              className='account-credits-block-edit-btn'
              aria-label='Edit general credits'
              disabled={isLoading}
              onClick={() => handleOpenModal('standard')}>
              <EditIcon iconSize='s' className='account-credits-block-edit-icon text-highlight' />
            </button>
          </div>

          <div className='account-credits-block-row'>
            <div className='account-credits-block-field'>
              <span className='account-credits-block-label'>{REFERRAL_CREDITS_TEXT}</span>
              <span className='account-credits-block-amount'>
                {isLoading ? '—' : convertMoneyToUSAFormat.format(referralAmount)}
              </span>
            </div>
            <button
              type='button'
              className='account-credits-block-edit-btn'
              aria-label='Edit referral credits'
              disabled={isLoading}
              onClick={() => handleOpenModal('referral')}>
              <EditIcon iconSize='s' className='account-credits-block-edit-icon text-highlight' />
            </button>
          </div>

          {programPackage && planBalance ?
            <div className='account-credits-block-row'>
              <div className='account-credits-block-field'>
                <span className='account-credits-block-label'>{programPackage.Name}</span>
                <span className='account-credits-block-amount'>{planBalance.remaining} visit(s) remaining</span>
              </div>
              {canEditPlan ?
                <button
                  type='button'
                  className='account-credits-block-edit-btn'
                  aria-label='Edit plan visits'
                  onClick={() => setIsPlanModalOpen(true)}>
                  <EditIcon iconSize='s' className='account-credits-block-edit-icon text-highlight' />
                </button>
              : null}
            </div>
          : null}

          <div className='account-credits-block-row account-credits-block-total-row'>
            <span className='account-credits-block-total-label'>{TOTAL_TEXT}</span>
            <span className='account-credits-block-total-amount'>
              {isLoading ? '—' : convertMoneyToUSAFormat.format(total)}
            </span>
          </div>
        </div>
      </EditCardAccountInfo>
    </>
  )
}
